import React, { useEffect, useCallback, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import useScreenSize from '../../hooks/useScreenSize';
import VoiceRecorder from './VoiceRecorder';
import VideoTranscriber from './VideoTranscriber';
import '../../styles/tiptap.css';

function RichTextEditor({ content, onChange, placeholder = 'Start writing your note...' }) {
  const { isMobile } = useScreenSize();
  const [isRecorderOpen, setIsRecorderOpen] = useState(false);
  const [isVideoTranscriberOpen, setIsVideoTranscriberOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
    ],
    content: content || '',
    editorProps: {
      attributes: {
        style: 'outline: none; min-height: 300px; padding: 0;',
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (onChange) {
        onChange(html);
      }
    },
  });

  // Update editor content when content prop changes from outside
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  const addImage = useCallback(() => {
    const url = window.prompt('Enter image URL:');
    if (url && editor) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  const uploadImage = useCallback((e) => {
    const file = e.target.files?.[0];
    if (file && editor) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target.result;
        editor.chain().focus().setImage({ src: base64 }).run();
      };
      reader.readAsDataURL(file);
    }
  }, [editor]);

  const openRecorder = useCallback(() => {
    setIsRecorderOpen(true);
  }, []);

  const closeRecorder = useCallback(() => {
    setIsRecorderOpen(false);
  }, []);

  const handleTranscriptionComplete = useCallback((text) => {
    if (editor && text) {
      // Insert transcribed text as new paragraph
      editor.chain().focus().insertContent(`<p>${text}</p>`).run();
    }
  }, [editor]);

  const openVideoTranscriber = useCallback(() => {
    setIsVideoTranscriberOpen(true);
  }, []);

  const closeVideoTranscriber = useCallback(() => {
    setIsVideoTranscriberOpen(false);
  }, []);

  const handleVideoTranscriptionComplete = useCallback((htmlContent) => {
    if (editor && htmlContent) {
      // Insert formatted HTML content
      editor.chain().focus().insertContent(htmlContent).run();
    }
  }, [editor]);

  if (!editor) {
    return null;
  }

  // Responsive button style
  const getButtonStyle = (isActive = false) => ({
    ...styles.button,
    minWidth: isMobile ? '44px' : '32px',
    height: isMobile ? '44px' : '32px',
    padding: isMobile ? '10px' : '7px 11px',
    fontSize: isMobile ? '16px' : '14px',
    ...(isActive ? styles.activeButton : {}),
  });

  return (
    <div style={styles.container}>
      {/* Toolbar */}
      <div
        className="tiptap-toolbar"
        style={{
          ...styles.toolbar,
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: isMobile ? '6px' : '8px',
          padding: isMobile ? '12px 8px' : '10px 12px',
          overflowX: isMobile ? 'hidden' : 'auto',
        }}
      >
        <div style={styles.buttonGroup}>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            style={getButtonStyle(editor.isActive('bold'))}
            title="Bold (Ctrl+B)"
            aria-pressed={editor.isActive('bold')}
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            style={getButtonStyle(editor.isActive('italic'))}
            title="Italic (Ctrl+I)"
            aria-pressed={editor.isActive('italic')}
          >
            <em>I</em>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            style={getButtonStyle(editor.isActive('strike'))}
            title="Strikethrough"
            aria-pressed={editor.isActive('strike')}
          >
            <s>S</s>
          </button>
        </div>

        {!isMobile && <div style={styles.separator} />}

        <div style={styles.buttonGroup}>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            style={getButtonStyle(editor.isActive('heading', { level: 1 }))}
            title="Heading 1"
            aria-pressed={editor.isActive('heading', { level: 1 })}
          >
            H1
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            style={getButtonStyle(editor.isActive('heading', { level: 2 }))}
            title="Heading 2"
            aria-pressed={editor.isActive('heading', { level: 2 })}
          >
            H2
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            style={getButtonStyle(editor.isActive('heading', { level: 3 }))}
            title="Heading 3"
            aria-pressed={editor.isActive('heading', { level: 3 })}
          >
            H3
          </button>
        </div>

        {!isMobile && <div style={styles.separator} />}

        <div style={styles.buttonGroup}>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            style={getButtonStyle(editor.isActive('bulletList'))}
            title="Bullet List"
            aria-pressed={editor.isActive('bulletList')}
          >
            •
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            style={getButtonStyle(editor.isActive('orderedList'))}
            title="Numbered List"
            aria-pressed={editor.isActive('orderedList')}
          >
            1.
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            style={getButtonStyle(editor.isActive('blockquote'))}
            title="Quote"
            aria-pressed={editor.isActive('blockquote')}
          >
            "
          </button>
        </div>

        {!isMobile && <div style={styles.separator} />}

        <div style={styles.buttonGroup}>
          <button
            type="button"
            onClick={addImage}
            style={getButtonStyle()}
            title="Insert Image URL"
          >
            🖼️
          </button>
          <label
            style={{
              ...getButtonStyle(),
              cursor: 'pointer',
            }}
            title="Upload Image"
          >
            📁
            <input
              type="file"
              accept="image/*"
              onChange={uploadImage}
              style={{ display: 'none' }}
            />
          </label>
          <button
            type="button"
            onClick={openRecorder}
            style={getButtonStyle()}
            title="Record Audio"
          >
            🎤
          </button>
          <button
            type="button"
            onClick={openVideoTranscriber}
            style={getButtonStyle()}
            title="Transcribe Video"
          >
            🎬
          </button>
        </div>

        {!isMobile && <div style={styles.separator} />}

        <div style={styles.buttonGroup}>
          <button
            type="button"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            style={getButtonStyle()}
            title="Undo (Ctrl+Z)"
          >
            ↶
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            style={getButtonStyle()}
            title="Redo (Ctrl+Y)"
          >
            ↷
          </button>
        </div>
      </div>

      {/* Editor Content */}
      <div style={styles.editorWrapper}>
        <EditorContent editor={editor} style={styles.editor} />
      </div>

      {/* Voice Recorder Modal */}
      <VoiceRecorder
        isOpen={isRecorderOpen}
        onClose={closeRecorder}
        onTranscriptionComplete={handleTranscriptionComplete}
      />

      {/* Video Transcriber Modal */}
      <VideoTranscriber
        isOpen={isVideoTranscriberOpen}
        onClose={closeVideoTranscriber}
        onTranscriptionComplete={handleVideoTranscriptionComplete}
      />
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 12px',
    borderBottom: '1px solid #e8e8e8',
    background: 'linear-gradient(to bottom, #fafafa, #f5f5f5)',
    borderRadius: '8px 8px 0 0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    overflowX: 'auto',
    overflowY: 'hidden',
  },
  buttonGroup: {
    display: 'flex',
    gap: '2px',
    background: '#f8fcfe',
    border: '1px solid #d9e9f2',
    borderRadius: '6px',
    padding: '3px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
  },
  separator: {
    width: '1px',
    height: '24px',
    background: '#d0d0d0',
    margin: '0 6px',
    flexShrink: 0,
  },
  button: {
    background: '#edf6fa',
    border: '1px solid #c7dce8',
    padding: '7px 11px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.15s ease',
    minWidth: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#444',
    position: 'relative',
    userSelect: 'none',
  },
  activeButton: {
    background: '#0778ad',
    color: 'white',
    boxShadow: '0 2px 4px rgba(8, 126, 184, 0.25)',
    transform: 'scale(1.05)',
  },
  uploadButton: {
    background: 'white',
    border: 'none',
    padding: '7px 11px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    transition: 'all 0.15s ease',
    minWidth: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#444',
    userSelect: 'none',
  },
  editorWrapper: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    background: 'white',
  },
  editor: {
    minHeight: '300px',
  },
};

export default RichTextEditor;
